import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Input, InputProps } from 'reactstrap';
import { useTranslation } from '@blocs.i18n';

/* ----- Types ----- */
type Props =
  | {
      defaultValue?: string;
      isValid?: boolean;
      maxLength?: number;
      customErrorMessage?: string;
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => any;
      onFocus?: (event: React.FocusEvent<HTMLInputElement>) => any;
      onBlur?: (event: React.FocusEvent<HTMLInputElement>) => any;
      name: string;
    }
  | Omit<InputProps, 'defaultValue'>;

/* ----- Styled Components ----- */
const InfoMessage = styled.div`
  color: ${(props): string => props.theme.colors.primary.black};
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 1rem;
  margin-top: 0.25rem;
  min-height: 1rem;
`;

const ErrorInfoMessage = styled(InfoMessage)`
  color: ${(props): string => props.theme.colors.accent.red};
`;

const StyledTextArea = styled(Input)`
  resize: none;
`;

/* ----- Main Component ----- */
/**
 * @deprecated This is an older component that does not use our standard form wrapper
 * to match the design system.
 *
 * If this component is used in application code, we want to replace it with a controlled
 * TextArea component instead, preferably inside a formik form context
 *
 * use the TextAreaInput
 * import { TextAreaInput } from '@minecraft.molecules'
 *
 * <TextAreaInput name="fieldName" label="Field Label" />
 */
const UncontrolledTextArea = React.memo(
  ({
    defaultValue = '',
    isValid = true,
    maxLength = Number.MAX_SAFE_INTEGER,
    onChange,
    onFocus,
    onBlur,
    customErrorMessage,
    name,
    ...restProps
  }: Props): JSX.Element => {
    const { t } = useTranslation();
    const [state, setState] = useState({
      isMaxLengthExceeded: defaultValue?.length >= maxLength,
      value: defaultValue,
      isFocus: false,
    });

    useEffect(() => {
      setState((prev) => ({ ...prev, value: defaultValue }));
    }, [defaultValue]);

    const formatTextAreaValue = useCallback(
      (textAreaValue: string): string => {
        const minAllowedCarriage = maxLength > 100 ? 3 : 1;
        const replacementOfCarriage = maxLength > 100 ? '\n\n' : ' ';
        const resultString = textAreaValue.replace(/^[\r\s]+/, '');
        const regex = new RegExp(`[\n\r]{${minAllowedCarriage},}`);

        return resultString.replace(regex, replacementOfCarriage);
      },
      [maxLength]
    );

    const handleValueChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        const textAreaValue: string = formatTextAreaValue(e.target.value);
        const nextLength = textAreaValue.length;
        const isExceeded = nextLength >= maxLength;

        setState((prev) => ({
          ...prev,
          isMaxLengthExceeded: isExceeded,
          value: isExceeded && nextLength > maxLength ? prev.value : textAreaValue,
        }));

        e.target.value = textAreaValue;
        onChange(e);
      },
      [formatTextAreaValue, maxLength, onChange]
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>): void => {
        setState((prev) => ({ ...prev, isFocus: false }));
        onBlur?.(e);
      },
      [onBlur]
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>): void => {
        setState((prev) => ({ ...prev, isFocus: true }));
        onFocus?.(e);
      },
      [onFocus]
    );

    const maxLengthInfo = useMemo(() => {
      if (!state.isFocus) return '';

      return state.isMaxLengthExceeded
        ? t('common:form.info.maxLengthReached', { maxLength })
        : t('common:form.info.maxLength', { maxLength });
    }, [state.isFocus, state.isMaxLengthExceeded, maxLength, t]);

    return (
      <>
        <StyledTextArea
          className={!isValid ? 'is-invalid' : ''}
          onChange={handleValueChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          maxLength={maxLength}
          value={state.value}
          type="textarea"
          id={name}
          name={name}
          {...restProps}
        />
        {maxLengthInfo && state.isMaxLengthExceeded && (
          <InfoMessage className="maxLengthInfo">{maxLengthInfo}</InfoMessage>
        )}
        {customErrorMessage && <ErrorInfoMessage>{customErrorMessage}</ErrorInfoMessage>}
      </>
    );
  }
);

UncontrolledTextArea.displayName = 'UncontrolledTextArea';

export default UncontrolledTextArea;
